import { fetchWithClerkJwt } from "./fetch-with-clerk";

/**
 * The **server's** rendering of the ACH authorization the consumer must assent
 * to. Displayed verbatim — never re-worded, re-formatted, or re-computed on the
 * client. The stored record has to be provably the text the consumer read, and
 * that only holds if exactly one place renders it.
 */
export type AchAuthorizationLegPreview = {
  legPurpose: string;
  amountCents: number;
  creditorLegalName: string;
  creditorLicenseNumber: string | null;
};

export type AchAuthorizationPreview = {
  authorizationText: string;
  authorizationVersion: string;
  authorizationTextSha256: string;
  timingDescription: string;
  totalAmountCents: number;
  deliveryFeeCents: number;
  coSurchargeCents: number;
  bankName: string | null;
  bankAccountLast4: string;
  legs: AchAuthorizationLegPreview[];
};

export class AchAuthorizationPreviewError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`ach_authorization_preview_${status}`);
    this.name = "AchAuthorizationPreviewError";
  }
}

export async function fetchAchAuthorizationPreview(
  getToken: () => Promise<string | null>,
  args: {
    dispensaryId: string;
    subtotalCents: number;
    taxCents?: number;
    coSurchargeCents?: number;
  },
): Promise<AchAuthorizationPreview> {
  const res = await fetchWithClerkJwt(getToken, "/api/v1/orders/ach-authorization-preview", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args),
  });

  if (!res.ok) {
    let code = "unknown";
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body?.error === "string") code = body.error;
    } catch {
      // Non-JSON error body; the status alone is enough to branch on.
    }
    throw new AchAuthorizationPreviewError(res.status, code);
  }

  return (await res.json()) as AchAuthorizationPreview;
}
